import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import useInfiniteFetch from '@hooks/useInfiniteFetch';
import { readCookie } from '@utils/cookies';
import { unstable_serialize } from 'swr/infinite';
import { sessionStates } from '@constants/index';
import getTextColor from 'aquilla/core/shared/utils/getTextColor';
import { nFormatter, pluralize } from '@utils/textHelper';
import { compareDates, dateWithTime, getDateMonthString } from '@utils/dates';
import { getIsOnlyRecordedClassesPresent } from '@comp/PlusCourseCard/util';
import usePresubscribedSpecificBlock from './usePresubscribedSpecificBlock';
import { getClassCard } from '../PopularClasses';

const blocksToBeRevalidate = [
  'carousel_classes',
  'courses_v2',
  'upcoming_classes'
];

const classVariant = {
  [sessionStates.FUTURE]: 'Upcoming',
  [sessionStates.LIVE]: 'Live',
  [sessionStates.PAST]: 'Completed',
  [sessionStates.DORMANT]: 'Dormont'
};

export function formatClassDetail(classInfo, hideViewCount) {
  const {
    author,
    name,
    topicGroups,
    languageDisplay,
    coverPhotoV1,
    nextSession,
    totalViews
  } = classInfo;
  const label = `Starts on ${dateWithTime(nextSession?.properties?.liveAt)}`;
  const variant = classVariant[nextSession?.properties?.state];
  const viewCount = hideViewCount
    ? null
    : totalViews || nextSession?.properties?.attendance || 0;

  const topicColor = topicGroups[0]?.name
    ? getTextColor(topicGroups[0]?.name[0])
    : '#8873FF';

  const classDetail = {
    ...classInfo,
    educatorName: `${author.firstName} ${author.lastName}`,
    educatorLink: `/@${author.username}`,
    title: name,
    topicName: topicGroups[0]?.name,
    topicColor,
    langLabel: languageDisplay,
    ...(variant && { variant }),
    thumbnail: getClassCard(coverPhotoV1, topicColor),
    viewCount: nFormatter(viewCount, 1),
    detailsLine: label
  };

  return classDetail;
}

const getFormattedClasses = (block = {}, hideViewCount = false) => {
  return {
    ...block,
    data: block?.data?.map((classData) =>
      formatClassDetail(classData, hideViewCount)
    )
  };
};

function formatEducator(educator) {
  const {
    firstName,
    lastName,
    totalWatchTime = 0,
    totalWatchMins,
    stats,
    liveMinutes,
    followersCount,
    bio,
    username,
    avatarV2,
    oldAvatar,
    avatar
  } = educator;

  const watchMins =
    Math.round(stats?.statistics?.TOTAL_WATCH_TIME_MINUTES / 60) ||
    Math.round(totalWatchTime / 60) ||
    totalWatchMins ||
    liveMinutes;
  const educatorInfo = {
    ...educator,
    educatorName: `${firstName} ${lastName}`,
    watchmins: `${nFormatter(watchMins)}`,
    followersCount: `${nFormatter(Math.floor(followersCount))}`,
    educatorBio: bio,
    properties: [
      { title: 'Watch Mins', value: `${nFormatter(watchMins)}` },
      { title: 'Followers', value: `${nFormatter(Math.floor(followersCount))}` }
    ],
    subjectInfo: {},
    educatorImage: avatarV2 || oldAvatar || avatar,
    isNew: !!avatarV2,
    href: `/@${username}`
  };

  return educatorInfo;
}
const getFormattedEducatorData = (block) => {
  return {
    ...block,
    data: block?.data?.map((educator) => formatEducator(educator))
  };
};

function formatCourse(course) {
  const {
    name,
    languageDisplay,
    topicGroups,
    author,
    coverPhoto,
    coverPhotoV1,
    isLive,
    startsAt,
    endsAt,
    programme,
    itemCount,
    isSpecial
  } = course;

  const isOnlyRecordedClassesPresent =
    getIsOnlyRecordedClassesPresent(programme) ||
    getIsOnlyRecordedClassesPresent(course);
  let label = '';
  if (isOnlyRecordedClassesPresent && !isSpecial) {
    label = `${pluralize(itemCount, 'session')}`;
  } else {
    const today = new Date();
    const startDateRef = compareDates(today, startsAt);
    const endDateRef = compareDates(today, endsAt);

    if (startDateRef > 0) label = `Starts on ${getDateMonthString(startsAt)}`;
    if (startDateRef === 0) label = 'Starts today';
    if (startDateRef < 0) label = 'In progress';
    if (endDateRef < 0) label = `Ended on ${getDateMonthString(endsAt)}`;
  }

  const courseInfo = {
    ...course,
    title: name,
    langLabel: languageDisplay,
    topicName: topicGroups[0]?.name,
    educatorName: `${author.firstName} ${author.lastName}`,
    educatorLink: `/@${author.username}`,
    courseThumbnail: coverPhotoV1 || coverPhoto,
    ...(isLive && { variant: 'live' }),
    detailsLine: label,
    topicColor: topicGroups[0]?.name
      ? getTextColor(topicGroups[0]?.name[0])
      : '#8873FF'
  };

  return courseInfo;
}

const getFormattedCourseData = (block) => {
  const courseBlock = block?.data[0] || {};
  const formattedData = [
    {
      ...courseBlock,
      data: courseBlock?.data?.map((course) => formatCourse(course))
    }
  ];
  return {
    ...block,
    data: formattedData
  };
};

const processFeedData = (data) => {
  // console.log("processFeedData", { data });
  const formattedData = data.map((block) => {
    switch (block.type) {
      case 'special_classes':
        return getFormattedClasses(block);
      case 'upcoming_classes':
        return getFormattedClasses(block);
      case 'carousel_classes':
        return getFormattedClasses(block);
      case 'educators':
        return getFormattedEducatorData(block);
      case 'courses_v2':
        return getFormattedCourseData(block);
      default:
        return block;
    }
  });
  return formattedData.filter((d) => d);
};

const getMapObject = (data) =>
  data?.reduce((acc, block) => {
    return { ...acc, [block.type]: block };
  }, {}) || [];

const mergeBlocks = ({
  subscribedBlocks = [],
  specificSubscribedBlocks = []
}) => {
  if (specificSubscribedBlocks.length > 0) {
    const specificSubscribedMappedBlocks = getMapObject(
      specificSubscribedBlocks
    );
    return subscribedBlocks?.map((block) => {
      if (blocksToBeRevalidate.includes(block?.type)) {
        return specificSubscribedMappedBlocks[block?.type] || block;
      }
      return block;
    });
  }
  return subscribedBlocks;
};

const getKey = (goalUID) =>
  goalUID ? `v1/feeds/${goalUID}/pre-subscription-home/` : null;

const usePresubscribedBlocks = (
  goalUID,
  options = { fallbackData: undefined, revalidateOnMount: false }
) => {
  const key = getKey(goalUID);
  const isLoggedIn = !!readCookie('accessToken');

  const hasBlocksToRevalidate = (options.fallbackData?.results || []).find(
    (block) => blocksToBeRevalidate.includes(block.type)
  );

  const { specificSubscribedBlocks } = usePresubscribedSpecificBlock(
    hasBlocksToRevalidate ? blocksToBeRevalidate : []
  );

  const { data, isValidating, ...rest } = useInfiniteFetch(
    key,
    { limit: 14 },
    {
      fallbackData: options.fallbackData ? [options.fallbackData] : undefined,
      revalidateOnMount: options.revalidateOnMount,
      revalidateFirstPage: false
    }
  );

  useFirstRenderSkip(() => {
    if (isLoggedIn) rest.mutate();
  }, [isLoggedIn]);

  const mergedData = mergeBlocks({
    subscribedBlocks: data.results,
    specificSubscribedBlocks
  });

  const finalData = {
    ...data,
    results: processFeedData(mergedData)
  };

  return {
    blockData: finalData,
    isValidating,
    ...rest,
    key: unstable_serialize(() => key)
  };
};

export default usePresubscribedBlocks;
